import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { ChevronLeft, ChevronRight, Percent, Clock } from 'lucide-react';
import {
  fetchProducts,
  selectAllDresses,
  selectDressesLoading,
  selectPagination,
} from '../store/slices/dressesSlice';
import ProductGrid from '../components/ProductGrid';
import { selectListPage, setListPage } from '../store/slices/filtersSlice';

const PAGE_SIZE = 12;

const DiscountedProductsPage = () => {
  const dispatch = useDispatch();
  const products = useSelector(selectAllDresses);
  const loading = useSelector(selectDressesLoading);
  const pagination = useSelector(selectPagination);

  const page = useSelector(state => selectListPage(state, 'discounts'));
  const setPage = (p) => dispatch(setListPage({ key: 'discounts', page: p }));

  useEffect(() => {
    dispatch(fetchProducts({
      onSale: true,
      page,
      pageSize: PAGE_SIZE,
    }));
  }, [dispatch, page]);

  const totalPages = Math.ceil(pagination.count / PAGE_SIZE);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
        <Link to="/" className="hover:text-primary-600 transition-colors">Store</Link>
        <span>/</span>
        <span className="text-gray-900 font-medium">Discounts</span>
      </nav>

      {/* Limited-time banner */}
      <div className="flex items-center gap-3 rounded-2xl bg-gradient-to-r from-red-500 to-rose-600 text-white px-5 py-4 mb-6 shadow-sm">
        <div className="w-10 h-10 rounded-full bg-white/15 flex items-center justify-center flex-shrink-0">
          <Percent className="w-5 h-5" />
        </div>
        <div>
          <h1 className="text-lg font-bold leading-tight">Limited-Time Discounts</h1>
          <p className="text-sm text-white/90 flex items-center gap-1.5 mt-0.5">
            <Clock className="w-3.5 h-3.5" />
            These prices are reduced for a limited time only — grab them before they're gone.
          </p>
        </div>
      </div>

      {!loading && (
        <p className="text-sm text-gray-500 mb-5">
          {pagination.count > 0
            ? `${pagination.count.toLocaleString()} product${pagination.count !== 1 ? 's' : ''} on sale`
            : 'No discounted products right now'}
        </p>
      )}

      <ProductGrid
        products={products}
        loading={loading}
        listId="discounts"
        listName="Discounts"
        startIndex={(page - 1) * PAGE_SIZE}
      />

      {!loading && totalPages > 1 && (
        <div className="flex items-center justify-center gap-2 mt-8">
          <button
            onClick={() => setPage(Math.max(1, page - 1))}
            disabled={page === 1}
            className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Prev</span>
          </button>
          <span className="text-sm text-gray-500 px-2">Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage(Math.min(totalPages, page + 1))}
            disabled={page === totalPages}
            className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-gray-600 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <span className="hidden sm:inline">Next</span>
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}
    </div>
  );
};

export default DiscountedProductsPage;
